import type { components } from 'admination-react/api-schema'
import { fillPath, getJson } from '@/core/api/http'

type Schemas = components['schemas']

export type SpalteStand = Schemas['SpalteStandResult']

export type DokumentSprachniveau = Schemas['DokumentSprachniveau']

export type SprachniveauStand = Schemas['SprachniveauStand']

export type SprachniveauAbweichung = Schemas['GrosseAbweichung']

export type SprachniveauQuellen = Schemas['SprachniveauQuellen']

export const SPRACHNIVEAU_DOKUMENTE = ['foerderplan', 'erstbericht', 'kompetenzbilanzierung'] as const satisfies readonly (keyof SprachniveauStand)[]

const MALFORMED = 'Die Antwort des Servers hatte nicht die erwartete Form.'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function isDokumentSprachniveau(value: unknown): value is DokumentSprachniveau | null {
  if (value === null) return true
  if (!isRecord(value)) return false
  const { veraltet, hinweise } = value
  return typeof veraltet === 'boolean' && Array.isArray(hinweise) && hinweise.every((h) => typeof h === 'string')
}

const ABWEICHENDE_QUELLEN = new Set<unknown>(['klett', 'aufnahme'] satisfies SprachniveauAbweichung['abweichend'])

const isNiveau = (value: unknown): value is string | null => value === null || typeof value === 'string'

export function isSprachniveauAbweichung(value: unknown): value is SprachniveauAbweichung | null {
  if (value === null) return true
  if (!isRecord(value)) return false
  const { sprachlehrkraft, klett, aufnahme, abweichend } = value
  return (
    typeof sprachlehrkraft === 'string' &&
    isNiveau(klett) &&
    isNiveau(aufnahme) &&
    Array.isArray(abweichend) &&
    abweichend.every((quelle) => ABWEICHENDE_QUELLEN.has(quelle))
  )
}

function isSprachniveauQuellen(value: unknown): value is SprachniveauQuellen | null {
  if (value === null) return true
  if (!isRecord(value)) return false
  return isNiveau(value.zertifikat) && isNiveau(value.klett) && isNiveau(value.sprachlehrkraft)
}

function isSprachniveauStand(value: unknown): value is SprachniveauStand {
  return isRecord(value) && SPRACHNIVEAU_DOKUMENTE.every((dokument) => isDokumentSprachniveau(value[dokument]))
}

function isSpalteStand(body: unknown): body is SpalteStand {
  if (!isRecord(body)) return false
  const { status, aufgabe, erledigt, sprachniveau, sprachniveau_abweichung, sprachniveau_quellen, sprachniveau_abweichung_quellen } = body
  return (
    typeof status === 'string' &&
    typeof aufgabe === 'string' &&
    Array.isArray(erledigt) &&
    erledigt.every((name) => typeof name === 'string') &&
    (sprachniveau === null || isSprachniveauStand(sprachniveau)) &&
    isSprachniveauAbweichung(sprachniveau_abweichung) &&
    isSprachniveauQuellen(sprachniveau_quellen) &&
    isSprachniveauQuellen(sprachniveau_abweichung_quellen)
  )
}

export async function getSpalteStand(coachingId: string): Promise<SpalteStand> {
  const body = await getJson<unknown>(fillPath('/spalte-stand/{coaching_id}', { coaching_id: coachingId }))
  if (!isSpalteStand(body)) throw new Error(MALFORMED)
  return body
}
